<template>
  <div class="min-h-screen flex bg-gray-50">
    <!-- Left Side - Background with RSS News -->
    <div class="hidden lg:flex lg:w-2/5 relative overflow-hidden">
      <!-- Beautiful Airplane Background -->
      <div 
        class="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style="background-image: url('https://selectair.be/media/selectair/media/selectair/testimages/sa-login.jpg?crop=0,0,0,0&height=&cropmode=percentage')"
      ></div>
      
      <!-- Elegant Overlay -->
      <div class="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-slate-800/70 to-gray-900/60"></div>
      
      <!-- Content -->
      <div class="relative z-10 flex flex-col h-full p-8 lg:p-12 text-white w-full">
        <!-- Header -->
        <div class="flex-shrink-0 mb-8">
          <div class="flex items-center space-x-4">
            <img 
              src="https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/uploads/icon-1741669842731.png" 
              alt="Selectair Logo" 
              class="w-20 h-20 rounded-2xl shadow-2xl bg-white/95 p-4"
            />
            <div>
              <h1 class="text-4xl font-bold text-white drop-shadow-lg">Selectair</h1>
              <p class="text-blue-100 text-xl font-medium">Admin Portal</p>
            </div>
          </div>
        </div>
        
        <!-- RSS Travel News Section -->
        <div class="flex-1 bg-white/5 backdrop-blur-md rounded-2xl p-6 shadow-2xl mb-6">
          <h2 class="text-2xl font-bold mb-6 flex items-center text-white">
            <div class="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"></path>
              </svg>
            </div>
            Latest Selectair News
          </h2>
          
          <!-- Loading State -->
          <div v-if="loadingFeed" class="space-y-4">
            <div v-for="i in 3" :key="i" class="animate-pulse">
              <div class="bg-white/10 rounded-xl p-4">
                <div class="h-4 bg-white/20 rounded w-3/4 mb-3"></div>
                <div class="h-3 bg-white/15 rounded w-1/2 mb-2"></div>
                <div class="h-3 bg-white/10 rounded w-full"></div>
              </div>
            </div>
          </div>
          
          <!-- RSS Feed Items -->
          <div v-else-if="rssItems.length > 0" class="h-full flex flex-col">
            <div class="space-y-4 flex-1 overflow-y-auto news-scrollbar pr-2" style="max-height: 65vh;">
              <a 
                v-for="(item, index) in rssItems.slice(0, 4)" 
                :key="index"
                :href="item.link"
                target="_blank"
                rel="noopener noreferrer"
                class="block bg-white/10 hover:bg-white/20 rounded-xl p-4 border border-white/10 hover:border-white/30 transition-all duration-300 cursor-pointer group transform  hover:shadow-xl"
              >
                <div class="flex items-start space-x-3">
                  <div class="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                    </svg>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="font-bold text-base line-clamp-2 mb-2 text-white group-hover:text-red-100 transition-colors leading-tight">{{ item.title }}</h3>
                    <div class="flex items-center space-x-2 text-xs text-red-100 mb-2">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <span class="font-medium">{{ formatDate(item.pubDate) }}</span>
                    </div>
                    <p v-if="item.description" class="text-white/90 text-xs line-clamp-2 leading-relaxed mb-3">{{ stripHtml(item.description) }}</p>
                    <div class="flex items-center text-red-200 text-xs font-semibold group-hover:text-red-100 transition-colors">
                      <span>Read article</span>
                      <svg class="w-3 h-3 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              </a>
            </div>

            <!-- View All News Link -->
            <div class="mt-4 pt-4 border-t border-white/20">
              <a 
                href="https://selectair-groep.uptodatesites.com/blog" 
                target="_blank"
                rel="noopener noreferrer"
                class="block text-center bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-3 px-4 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                <div class="flex items-center justify-center space-x-2">
                  <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"></path>
                  </svg>
                  <span>Explore All Selectair News</span>
                </div>
              </a>
            </div>
          </div>
          
          <!-- Error State -->
          <div v-else class="text-center py-8">
            <div class="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"></path>
              </svg>
            </div>
            <p class="text-white text-lg font-medium">Stay tuned for travel updates</p>
            <p class="text-white/70 text-sm mt-2">Latest news will appear here</p>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="flex-shrink-0 text-center text-white/60">
          <p class="text-sm">© 2025 Selectair Group. All rights reserved.</p>
        </div>
      </div>
    </div>

    <!-- Right Side - Clean Login Form -->
    <div class="w-full lg:w-3/5 flex items-center justify-center p-8 lg:p-12 bg-white">
      <div class="max-w-xl w-full">
        <!-- Mobile Header -->
        <div class="lg:hidden mb-8 text-center">
          <img 
            src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEjWba9cb_pkejMGMH7qatIVTvTdJVvF1NZr87pE6PCGXleLLQ1C-68SwWp72ufKhhOXcn0aQthUOqxtQ6Fqn67XnrX2UeWRP85AyKc5nwEKdbAwEQaUTgB4KsXj5iAJqN_LjWNa8G2nFy4tmnz-p2dRlzjbQHqqm_ZL0wEcFSyMYazXkLAF65GcY2wm2VU/s0/_logo-1741262832124.png" 
            alt="Selectair Logo" 
            class=" shadow-lg bg-white mx-auto mb-4 p-2"
          />
          <h1 class="text-2xl font-bold text-gray-900">Selectair</h1>
          <p class="text-gray-600">Admin Portal</p>
        </div>

        <!-- Login Header with Logo -->
        <div class="text-center mb-10">
          <div class="hidden lg:block mb-8">
            <img 
              src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEjWba9cb_pkejMGMH7qatIVTvTdJVvF1NZr87pE6PCGXleLLQ1C-68SwWp72ufKhhOXcn0aQthUOqxtQ6Fqn67XnrX2UeWRP85AyKc5nwEKdbAwEQaUTgB4KsXj5iAJqN_LjWNa8G2nFy4tmnz-p2dRlzjbQHqqm_ZL0wEcFSyMYazXkLAF65GcY2wm2VU/s0/_logo-1741262832124.png" 
              alt="Selectair Logo" 
              class=" bg-white mx-auto mb-4 p-4"
            />
          </div>
          <h2 class="text-4xl font-bold text-gray-900 mb-3">
            Welcome Back
          </h2>
          <p class="text-lg text-gray-600">
            Sign in to access your admin dashboard
          </p>
        </div>
      
        <!-- Login Form -->
        <form class="space-y-6" @submit.prevent="handleLogin">
          <div class="space-y-5">
            <div>
              <label for="email" class="block text-sm font-semibold text-gray-800 mb-3">Email Address</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"></path>
                  </svg>
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autocomplete="email"
                  required
                  class="appearance-none relative block w-full pl-12 pr-4 py-4 border-2 border-gray-200 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg bg-gray-50 hover:bg-white focus:bg-white"
                  placeholder="Enter your email address"
                  v-model="email"
                  :disabled="loading"
                />
              </div>
            </div>
            
            <div>
              <label for="password" class="block text-sm font-semibold text-gray-800 mb-3">Password</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autocomplete="current-password"
                  required
                  class="appearance-none relative block w-full pl-12 pr-4 py-4 border-2 border-gray-200 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-lg bg-gray-50 hover:bg-white focus:bg-white"
                  placeholder="Enter your password"
                  v-model="password"
                  :disabled="loading"
                />
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between pt-2">
            <div class="flex items-center">
              <input
                id="keep-signed-in"
                name="keep-signed-in"
                type="checkbox"
                class="h-8 w-8 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                v-model="keepMeSignedIn"
                :disabled="loading"
              />
              <label for="keep-signed-in" class="ml-3 block text-sm font-medium text-gray-700">
                Keep me signed in
              </label>
            </div>
            <div class="text-sm">
              <router-link to="/admin/forgot-password" class="font-medium text-red-600 hover:text-red-500">
                Forgot password?
              </router-link>
            </div>
          </div>

          <!-- Alert Message -->
          <div v-if="showAlert" class="rounded-xl p-4" :class="alertClass">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg v-if="loginInfo === 'success'" class="h-6 w-6 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                </svg>
                <svg v-else class="h-6 w-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm font-semibold" :class="alertTextClass">
                  {{ alertMessage }}
                </p>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="pt-4">
            <button
              type="submit"
              :disabled="loading"
              class="group relative w-full flex justify-center py-4 px-6 border border-transparent text-lg font-bold rounded-xl text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-4 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              <span v-if="loading" class="absolute left-0 inset-y-0 flex items-center pl-4">
                <svg class="animate-spin h-6 w-6 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              <div class="flex items-center space-x-3">
                <svg v-if="!loading" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
                </svg>
                <span>{{ loading ? 'Signing in...' : 'Sign in to Admin Panel' }}</span>
              </div>
            </button>
          </div>
        </form>
        
        <!-- Footer for Mobile -->
        <div class="lg:hidden text-center mt-8 text-gray-500">
          <p class="text-sm">© 2025 Selectair Group. All rights reserved.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdminLogin',
  data() {
    return {
      email: '',
      password: '',
      keepMeSignedIn: false,
      loading: false,
      showAlert: false,
      alertMessage: 'Invalid email or password. Please try again.',
      loginInfo: 'error',
      rssItems: [],
      loadingFeed: true
    }
  },
  computed: {
    shopSettings() {
      return this.$root.shopSettings || {};
    },
    apiUrl() {
      return this.shopSettings?.apiUrl || "https://selectair-vakanties.uptodatesites.com";
    },
    alertClass() {
      return this.loginInfo === 'success' 
        ? 'bg-green-50 border border-green-200' 
        : 'bg-red-50 border border-red-200'
    },
    alertTextClass() {
      return this.loginInfo === 'success' 
        ? 'text-green-800' 
        : 'text-red-800'
    }
  },
  mounted() {
    // Check if already logged in
    const token = localStorage.getItem('admin_token')
    if (token) {
      this.verifyToken(token)
    }
    
    // Fetch RSS feed
    this.fetchRssFeed()
  },
  methods: {
    async verifyToken(token) {
      try {
        const response = await axios.get(`${this.apiUrl}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (response.data.success) {
          this.$router.push('/admin/dashboard')
        }
      } catch (error) {
        // Token invalid, remove it
        localStorage.removeItem('admin_token')
      }
    },
    async handleLogin() {
      this.loading = true
      this.showAlert = false
      
      try {
        const response = await axios.post(`${this.apiUrl}/api/admin/login`, {
          isOAuth: false,
          email: this.email,
          password: this.password,
          keepMeSignedIn: this.keepMeSignedIn
        })

        if (response.data.success) {
          this.loginInfo = 'success'
          this.alertMessage = 'Success! Redirecting to dashboard...'
          this.showAlert = true
          
          // Store the admin token
          localStorage.setItem('admin_token', response.data.token)
          localStorage.setItem('uptodate_token', response.data.uptodateToken)
          localStorage.setItem('admin_user', JSON.stringify(response.data.user))
          
          setTimeout(() => {
            this.loading = false
            this.$router.push('/admin/dashboard')
          }, 2000)
        } else {
          this.loginInfo = 'error'
          this.alertMessage = response.data.message || 'Login failed'
          this.showAlert = true
          this.loading = false
        }
      } catch (error) {
        this.loginInfo = 'error'
        this.alertMessage = error.response?.data?.message || 'Network error occurred'
        this.showAlert = true
        this.loading = false
      }
    },
    async fetchRssFeed() {
      try {
        this.loadingFeed = true
        const response = await axios.get(`${this.apiUrl}/api/admin/rss-feed`)
        
        if (response.data.success) {
          this.rssItems = response.data.data.items
        }
      } catch (error) {
        console.error('Error fetching RSS feed:', error)
        // Set some sample data as fallback
        // this.rssItems = [
        //   { title: 'Selectair Admin System Update', pubDate: new Date().toISOString(), description: 'New file management features available', link: 'https://selectair-groep.uptodatesites.com' },
        //   { title: 'Security Enhancement', pubDate: new Date().toISOString(), description: 'Enhanced authentication and access controls', link: 'https://selectair-groep.uptodatesites.com' },
        //   { title: 'Performance Improvements', pubDate: new Date().toISOString(), description: 'Faster file uploads and downloads', link: 'https://selectair-groep.uptodatesites.com' }
        // ]
      } finally {
        this.loadingFeed = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        })
      } catch {
        return ''
      }
    },
    stripHtml(text) {
      if (!text) return ''
      return text.replace(/<[^>]*>/g, '').substring(0, 100) + (text.length > 100 ? '...' : '')
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Beautiful scrollbar for news feed */
.news-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.news-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.news-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.news-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Animation for loading states */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>